import { inject } from '@angular/core';
import { Router, CanActivateFn, ActivatedRouteSnapshot } from '@angular/router';
import { AuthStore } from '../../stores/auth.store';
import { AuthService } from '../services/auth.service';
import { AppStorageService } from '../services/app-storage.service';

/**
 * COLD-LOAD GUARD-RACE FIX.
 *
 * The role/permission guards evaluate requirements SYNCHRONOUSLY against
 * AuthStore.userRoles() (<- currentUser().roles), which is hydrated
 * ASYNCHRONOUSLY by AuthService.loadEffectivePermissions(). On a cold load (or
 * during a tenant-switch permission reload) route activation can race that
 * hydration, so a privileged user is denied (-> /unauthorized) purely on timing.
 *
 * This helper makes a guard AWAIT AuthService.authReady (the "roles are
 * currently trustworthy" gate) before it evaluates, but ONLY when the user is
 * logged in and hydration has not yet settled. When already ready it returns
 * immediately (no regression for warm navigation). It never blocks an
 * unauthenticated user — callers handle that BEFORE invoking this.
 *
 * A finite timeout inside whenAuthReady() guarantees the navigation can never
 * hang: on timeout the guard evaluates against whatever roles exist and fails
 * SAFE (deny).
 */
async function ensureAuthHydrated(authService: AuthService): Promise<void> {
  if (authService.authReady()) {
    return;
  }
  await authService.whenAuthReady();
}

// GAP-FE-C013: the post-login return URL is session-scoped (consumed once after
// login, never persisted across sessions). The writes below go through
// AppStorageService's session methods (sessionStorage-backed) so they are
// SSR-safe and mockable — no longer raw sessionStorage. Session scope is
// preserved (setSessionItem writes to sessionStorage, not localStorage).

/**
 * Sanitize a return URL before storing it in sessionStorage.
 *
 * Defends against:
 * - Open-redirect via protocol-relative URLs (`//evil.com`).
 * - Open-redirect via backslash-prefixed paths (`/\evil.com`); some
 *   browsers normalise `\` to `/`, yielding effective `//evil.com`.
 * - Open-redirect via percent-encoded bypass sequences (`%2f%2f`,
 *   `%5c`); decoded once before the structural checks.
 * - API-endpoint redirect (`/api/...`).
 * - Credential leakage in sessionStorage: unknown query string and URL
 *   fragment are stripped so OAuth `code`/`state`/`access_token`
 *   never persist to storage just because the user happened to be
 *   on a callback URL when the auth check fired.
 *
 * Shared across authGuard, roleGuard, and permissionGuard for consistency.
 */
export function sanitizeGuardReturnUrl(url: string): string {
  if (!url) return '/dashboard';

  // Decode once so attackers cannot bypass structural checks via
  // %2f%2f / %5c-style escaping. decodeURIComponent throws on a
  // malformed escape; a malformed return URL is treated as unsafe.
  let decoded: string;
  try {
    decoded = decodeURIComponent(url);
  } catch {
    return '/dashboard';
  }

  if (
    !decoded.startsWith('/') ||
    decoded.startsWith('//') ||
    decoded.startsWith('/\\') ||
    decoded.includes('/api/')
  ) {
    return '/dashboard';
  }

  // Strip query string and fragment so credentials never persist to
  // sessionStorage. Path-only is sufficient for post-login navigation.
  const queryIdx = decoded.indexOf('?');
  const hashIdx = decoded.indexOf('#');
  let cutAt = decoded.length;
  if (queryIdx >= 0) cutAt = Math.min(cutAt, queryIdx);
  if (hashIdx >= 0) cutAt = Math.min(cutAt, hashIdx);
  return decoded.slice(0, cutAt);
}

/**
 * Role-based authorization guard to protect routes based on user roles
 * Uses signal-based AuthStore for role checking
 */
export const roleGuard: CanActivateFn = async (route: ActivatedRouteSnapshot, state) => {
  const authStore = inject(AuthStore);
  const authService = inject(AuthService);
  const router = inject(Router);
  const storage = inject(AppStorageService);

  // Check authentication first — NEVER block an unauthenticated user; redirect
  // immediately so login is never delayed by the hydration gate below.
  if (!authStore.isLoggedIn()) {
    // User not authenticated, redirect to login
    // Store sanitized return URL in sessionStorage only (consistent with authGuard)
    storage.setSessionItem('auth_return_url', sanitizeGuardReturnUrl(state.url));
    router.navigate(['/auth/login']);
    return false;
  }

  const requiredRoles = route.data?.['requiredRoles'] as string[];
  if (!requiredRoles || requiredRoles.length === 0) {
    // No specific roles required
    return true;
  }

  // COLD-LOAD RACE FIX: wait for role/permission hydration to settle before
  // evaluating, so a privileged user is not denied because the guard ran before
  // loadEffectivePermissions() populated currentUser().roles.
  await ensureAuthHydrated(authService);

  // Check if user has any of the required roles
  const hasRequiredRole = authStore.hasAnyRole()(requiredRoles);

  if (hasRequiredRole) {
    return true;
  } else {
    // User doesn't have required role, redirect to unauthorized page
    router.navigate(['/unauthorized']);
    return false;
  }
};

/**
 * Permission-based authorization guard for fine-grained access control
 * Uses signal-based AuthStore for permission and role checking
 *
 * Checks route.data for:
 * - requiredPermissions: string[] - User needs ANY of these permissions
 * - requiredRoles: string[] - User needs ANY of these roles
 *
 * Access is granted if user has ANY required permission OR ANY required role.
 * This allows flexible RBAC where roles can bypass permission checks.
 */
export const permissionGuard: CanActivateFn = async (route: ActivatedRouteSnapshot, state) => {
  const authStore = inject(AuthStore);
  const authService = inject(AuthService);
  const router = inject(Router);
  const storage = inject(AppStorageService);

  // Check authentication first — NEVER block an unauthenticated user; redirect
  // immediately so login is never delayed by the hydration gate below.
  if (!authStore.isLoggedIn()) {
    // Store sanitized return URL in sessionStorage only (consistent with authGuard)
    storage.setSessionItem('auth_return_url', sanitizeGuardReturnUrl(state.url));
    router.navigate(['/auth/login']);
    return false;
  }

  const requiredPermissions = route.data?.['requiredPermissions'] as string[] | undefined;
  const requiredRoles = route.data?.['requiredRoles'] as string[] | undefined;

  // If no permissions or roles required, allow access
  if ((!requiredPermissions || requiredPermissions.length === 0) &&
      (!requiredRoles || requiredRoles.length === 0)) {
    return true;
  }

  // COLD-LOAD RACE FIX: wait for role/permission hydration to settle before
  // evaluating, so a privileged user is not denied because the guard ran before
  // loadEffectivePermissions() populated currentUser().roles/.permissions.
  await ensureAuthHydrated(authService);

  // Check if user has any of the required roles (roles can bypass permission checks)
  if (requiredRoles && requiredRoles.length > 0) {
    const hasRequiredRole = authStore.hasAnyRole()(requiredRoles);
    if (hasRequiredRole) {
      return true;
    }
  }

  // Check if user has any of the required permissions
  if (requiredPermissions && requiredPermissions.length > 0) {
    const hasRequiredPermission = authStore.hasAnyPermission()(requiredPermissions);
    if (hasRequiredPermission) {
      return true;
    }
  }

  // User doesn't have required role or permission, redirect to unauthorized page
  router.navigate(['/unauthorized']);
  return false;
};